import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowRight, BookOpen, CalendarDays, Clock3, Flame, Star } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { useAuth } from "@/components/auth-context";
import { EmptyState, LoadingLine, PageHeader } from "@/components/primitives";
import { apiFetch } from "@/lib/api";
import { dashboardDayKey, millisecondsUntilNextShanghaiDay } from "@/lib/dashboard-day";

interface DashboardData {
  today: {
    reviewCount: number;
    targetCount: number;
    reviewTaskSize: number;
    initialReview: number;
    initialNew: number;
    scopeLabels: string[];
  };
  personalWords: number;
  favorites: number;
  mistakes: number;
  streak: number;
  memory: {
    targetRetention: number;
    currentRetention: number | null;
    curve: Array<{ date: string; retention: number | null }>;
    forecast: Array<{ date: string; count: number }>;
  };
  dailySentence: { sentence: string; year: number; examCode: string; examType: string; section: string; label?: string | null } | null;
}

function MemoryOverview({ memory }: { memory: DashboardData["memory"] }) {
  const points = memory.curve.map((point, index) => ({
    ...point,
    x: 42 + index * 86,
    y: point.retention === null ? null : 18 + (100 - point.retention) * 1.22,
  }));
  const line = points.filter((point): point is typeof point & { y: number } => point.y !== null)
    .map((point) => `${point.x},${point.y}`).join(" ");
  const maxForecast = Math.max(1, ...memory.forecast.map((point) => point.count));
  return (
    <div className="memory-overview">
      <div className="retention-chart">
        {line ? (
          <svg viewBox="0 0 600 170" role="img" aria-label="未来七天平均记忆保持率">
            <line x1="28" y1={18 + (100 - memory.targetRetention) * 1.22} x2="572" y2={18 + (100 - memory.targetRetention) * 1.22} className="retention-chart__target" />
            <text x="568" textAnchor="end" y={12 + (100 - memory.targetRetention) * 1.22}>{memory.targetRetention}% 目标保持率</text>
            <polyline points={line} className="retention-chart__line" />
            {points.map((point) => point.y === null ? null : <circle key={point.date} cx={point.x} cy={point.y} r="4" />)}
          </svg>
        ) : <div className="memory-overview__empty">完成第一组背诵后生成个人保持率曲线</div>}
      </div>
      <div className="forecast-chart" aria-label="未来七天到期复习量">
        {memory.forecast.map((point) => {
          const date = new Date(`${point.date}T00:00:00`);
          const weekday = new Intl.DateTimeFormat("zh-CN", { weekday: "short" }).format(date).replace("周", "");
          return (
            <div className="forecast-day" key={point.date} title={`${point.date}：${point.count} 个到期词`}>
              <strong>{point.count}</strong>
              <div><span style={{ height: `${Math.max(point.count ? 8 : 0, point.count / maxForecast * 100)}%` }} /></div>
              <small>{weekday}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const [dayKey, setDayKey] = useState(() => dashboardDayKey());
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDayKey(dashboardDayKey()),
      millisecondsUntilNextShanghaiDay() + 250,
    );
    return () => window.clearTimeout(timer);
  }, [dayKey]);
  const query = useQuery({
    queryKey: ["dashboard", dayKey],
    queryFn: () => apiFetch<DashboardData>("/dashboard"),
    staleTime: 30_000,
  });
  if (query.isLoading) return <div className="page"><LoadingLine /></div>;
  if (!query.data) return <div className="page"><EmptyState title="暂时无法读取学习数据" description="请检查 API 与数据库连接后刷新页面。" /></div>;

  const data = query.data;
  const now = new Date();
  const greeting = now.getHours() < 12 ? "早上好" : now.getHours() < 18 ? "下午好" : "晚上好";
  const dateLabel = new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", weekday: "long" }).format(now);
  const canReview = data.today.initialReview > 0;
  const canLearn = data.today.initialNew > 0;
  const scope = data.today.scopeLabels.join(" + ") || "当前词库";

  return (
    <div className="page dashboard-page">
      <PageHeader
        title={greeting + "，" + (user?.displayName ?? "学习者")}
        description="查看今日新词、到期复习与学习进度。"
        action={<div className="date-label"><CalendarDays /> {dateLabel}</div>}
      />

      <section className="dashboard-lead" aria-labelledby="today-title">
        <div className="today-copy">
          <p className="section-label">今日学习</p>
          <h2 id="today-title">复习和新词分组进行。</h2>
          <p>{canReview || canLearn ? `今天有 ${data.today.reviewCount} 个待复习词；新词从 ${data.today.initialNew} 个起步，再根据回忆表现动态补充。` : "当前没有待复习词或可学习的新词，可以进入设置调整学习范围。"}</p>
          <div className="dashboard-study-actions">
            <Link to="/app/$subject/review" params={{ subject: "english" }} search={{ mode: "review" }} className="button button--primary button--lg">
              {canReview ? "开始今日复习" : "查看复习"} <ArrowRight />
            </Link>
            <Link to="/app/$subject/review" params={{ subject: "english" }} search={{ mode: "new_words" }} className="button button--secondary button--lg">
              {canLearn ? "学习新词" : "调整新词范围"} <BookOpen />
            </Link>
          </div>
        </div>
        <div className="today-plan" aria-label="今日学习数据">
          <div className="today-plan__heading"><span>今日两种模式</span><small>{scope}</small></div>
          <div className="today-numbers">
            <div><strong>{data.today.initialReview}</strong><span>本组复习</span></div>
            <div><strong>{data.today.initialNew}</strong><span>首批新词</span></div>
            <div><strong>{data.today.targetCount}</strong><span>新词目标</span></div>
          </div>
        </div>
      </section>

      <section className="dashboard-summary" aria-labelledby="summary-title">
        <div className="dashboard-section-heading">
          <div><p className="section-label">词汇管理</p><h2 id="summary-title">词汇分类</h2></div>
        </div>
        <div className="dashboard-shortcuts">
          <Link to="/app/$subject/word-library" params={{ subject: "english" }} className="dashboard-shortcut">
            <span className="dashboard-shortcut__icon"><BookOpen /></span>
            <span className="dashboard-shortcut__copy"><strong>阅读生词</strong><small>管理手动与拍照录入的词汇</small></span>
            <span className="dashboard-shortcut__value">{data.personalWords}</span>
            <ArrowRight className="dashboard-shortcut__arrow" />
          </Link>
          <Link to="/app/$subject/favorites" params={{ subject: "english" }} className="dashboard-shortcut">
            <span className="dashboard-shortcut__icon"><Star /></span>
            <span className="dashboard-shortcut__copy"><strong>收藏词汇</strong><small>回看查词时标记的重点</small></span>
            <span className="dashboard-shortcut__value">{data.favorites}</span>
            <ArrowRight className="dashboard-shortcut__arrow" />
          </Link>
          <Link to="/app/$subject/mistakes" params={{ subject: "english" }} className="dashboard-shortcut">
            <span className="dashboard-shortcut__icon"><Clock3 /></span>
            <span className="dashboard-shortcut__copy"><strong>近期错词</strong><small>集中处理尚未掌握的词</small></span>
            <span className="dashboard-shortcut__value">{data.mistakes}</span>
            <ArrowRight className="dashboard-shortcut__arrow" />
          </Link>
        </div>
      </section>

      <section className="dashboard-lower">
        <article className="progress-section">
          <div className="section-heading-row">
            <div><p className="section-label">学习统计</p><h2>记忆保持率</h2></div>
            <div className="memory-heading-metrics">
              <span><strong>{data.memory.currentRetention === null ? "--" : `${Math.round(data.memory.currentRetention)}%`}</strong><small>当前保持率</small></span>
              <span className="streak"><Flame /><strong>{data.streak}</strong><small>连续天数</small></span>
            </div>
          </div>
          <MemoryOverview memory={data.memory} />
        </article>

        <aside className="daily-sentence">
          <div className="daily-sentence__mark" aria-hidden="true">“</div>
          {data.dailySentence ? (
            <div>
              <p className="section-label">每日一句</p>
              <blockquote>{data.dailySentence.sentence}</blockquote>
              <cite>— {data.dailySentence.year} {data.dailySentence.examType} · {data.dailySentence.section}{data.dailySentence.label ? " " + data.dailySentence.label : ""}</cite>
            </div>
          ) : (
            <div><p className="section-label">每日一句</p><p>真题语料尚未发布。</p></div>
          )}
        </aside>
      </section>
    </div>
  );
}
